/*
 * Layers PDP recommendations and featured grids.
 *
 * Each `layers-related-products` element fetches a Layers block
 * (sdk.blocks({ blockId, anchorId })) anchored to the current product and
 * renders product cards into the section's mobile carousel and desktop grid,
 * reusing the existing recommendation-tabs / related-products markup slots.
 *
 * `layers-block` is the same element for featured grids; without a block id it
 * falls back to the Layers collection (sdk.collection({ collectionHandle })),
 * in-stock products only, matching the Liquid featured-collections loop.
 */

import { getSDK, mediaResponseOptions, slimProducts, whenNearViewport } from './layers.js';
import { renderProductCard, revealCards } from './layers-card.js';

const BLOCK_LIMIT = 10;

export class LayersRelatedProducts extends HTMLElement {
  connectedCallback() {
    if (this.connected) {
      // Moved in the DOM before its fetch started: wait again.
      if (this.awaitingFetch) this.deferFetch();
      return;
    }
    this.connected = true;

    this.blockId = this.dataset.blockId;
    this.collectionHandle = this.dataset.collectionHandle;
    this.anchorId = this.dataset.anchorId;
    this.limit = Math.max(1, Number(this.dataset.limit) || BLOCK_LIMIT);
    this.sectionId = this.dataset.sectionId || 'related';
    this.hosts = Array.from(this.querySelectorAll('[data-layers-block]'));

    if ((!this.blockId && !this.collectionHandle) || this.hosts.length === 0) {
      this.hideEmpty();
      return;
    }

    this.awaitingFetch = true;
    this.deferFetch();
  }

  /*
   * Fetch once the section nears the viewport. The section is watched rather
   * than this element: the hosts stay hidden (zero size) until filled, and a
   * row in an inactive tab or carousel cell still loads with its section.
   */
  deferFetch() {
    const target = this.closest('.shopify-section') || this.closest('section') || this;
    this.cancelDeferredFetch = whenNearViewport(target, () => {
      this.awaitingFetch = false;
      this.fetchBlock();
    });
  }

  disconnectedCallback() {
    this.cancelDeferredFetch?.();
  }

  async fetchBlock() {
    let sdk;
    try {
      sdk = await getSDK();
    } catch (error) {
      console.error('Layers SDK failed to initialize:', error);
      this.hideEmpty();
      return;
    }

    const pagination = { limit: this.limit, page: 1 };
    // Recommendation rows and featured grids sit below the first screen, so
    // skip Layers' high-priority preload hints for them.
    const responseOptions = mediaResponseOptions({ enabled: false });
    let data, error;
    try {
      if (this.blockId) {
        const controller = sdk.blocks({
          blockId: this.blockId,
          anchorId: this.anchorId || undefined,
          transformBody: slimProducts(),
        });
        ({ data, error } = await controller.get({ pagination, responseOptions }));
      } else {
        const controller = sdk.collection({ collectionHandle: this.collectionHandle, transformBody: slimProducts() });
        ({ data, error } = await controller.get({ pagination, forceHideOutOfStock: true, responseOptions }));
      }
    } catch (requestError) {
      console.error('Layers block request failed:', requestError);
      this.hideEmpty();
      return;
    }

    if (error) {
      console.error('Layers block request failed:', error);
      this.hideEmpty();
      return;
    }

    const products = (data?.products || []).slice(0, this.limit);
    if (products.length === 0) {
      this.hideEmpty();
      return;
    }

    const section = this.closest('section');
    const perRowDesktop = Number(section?.dataset?.productsPerRowDesktop) || 4;
    const hideProductInformation = this.dataset.hideProductInformation === 'true';
    const reveal = this.dataset.reveal === 'true';

    // A carousel's <scroll-carousel> sits inside a <product-list> wrapper, so
    // look for it first; a combined selector would return the outer wrapper.
    const cardTarget = (root) =>
      root.querySelector('scroll-carousel') || root.querySelector('product-list, .product-list-grid');

    const fill = (target, listMode) => {
      const cards = products.map((product) =>
        renderProductCard(product, {
          stacked: listMode === 'grid',
          reveal,
          hideProductInformation,
          sectionId: `${this.sectionId}-${listMode}`,
          productsPerRowDesktop: listMode === 'grid' ? perRowDesktop : 2.4,
          productsPerRowMobile: 2.4,
        }),
      );
      target.append(...cards);
      return cards;
    };

    this.hosts.forEach((host) => {
      const listMode = host.dataset.listMode || 'carousel';
      // Deferred markup lives in <template>; fill the cloned fragment before it
      // connects so scroll-carousel upgrades with cells already present (theme.js
      // carousels assume non-empty cells).
      const template = host.querySelector(':scope > template');
      if (template) {
        const fragment = template.content.cloneNode(true);
        const target = cardTarget(fragment) || fragment.firstElementChild;
        if (target) fill(target, listMode);
        // Drop server-rendered placeholders now that real cards are ready.
        host.querySelectorAll(':scope > .layers-skeleton-list').forEach((placeholder) => placeholder.remove());
        host.appendChild(fragment);
        template.remove();
      } else {
        // No <product-list> connects after these cards land, so reveal them here.
        const target = cardTarget(host) || host;
        target.replaceChildren();
        revealCards(fill(target, listMode));
      }

      host.hidden = false;
    });
  }

  hideEmpty() {
    // data-empty lets a listener installed after this point still see it; the
    // event notifies the host section so the Related tab can hide.
    this.setAttribute('data-empty', '');
    this.dispatchEvent(new CustomEvent('layers-block-empty', { bubbles: true }));
    this.hosts.forEach((host) => (host.hidden = true));
    this.hidden = this.querySelectorAll('[data-layers-block]:not([hidden])').length === 0;

    if (this.dataset.hideSectionWhenEmpty === 'true') {
      const section = this.closest('.shopify-section');
      if (section) section.hidden = true;
    }

    // Featured-grids usage: hide the carousel item and its nav button.
    const item = this.closest('[data-layers-item]');
    if (!item) return;
    const itemKey = item.dataset.layersItem;
    item.hidden = true;
    const scope = this.closest('.section-spacing') || this.closest('section') || document;
    const myButton = Array.from(scope.querySelectorAll('[data-layers-nav-item]')).find(
      (button) => button.dataset.layersNavItem === itemKey,
    );
    if (myButton) myButton.hidden = true;
    syncCarouselSelection(item.parentElement);
  }
}

/*
 * Prestige carousels index only visible cells, so hiding one shifts every
 * later index, and when the hidden cell was the selected first one, select(0)
 * returns early because the index didn't change. Keep the selected cell (or
 * fall back to the first visible one), move `is-selected` to it, and let the
 * carousel re-derive its index from that class (its connectedCallback does
 * only that), then point the navigation at the same index.
 */
export function syncCarouselSelection(carousel) {
  if (!carousel) return;
  const cells = Array.from(carousel.children).filter((cell) => cell.hasAttribute('data-layers-item'));
  const visible = cells.filter((cell) => !cell.hidden);
  const selected = visible.find((cell) => cell.classList.contains('is-selected')) || visible[0];
  if (!selected) return;

  cells.forEach((cell) => cell.classList.toggle('is-selected', cell === selected));
  // A cell that was animated out keeps its inline end state.
  selected.style.removeProperty('opacity');
  selected.style.removeProperty('transform');
  carousel.connectedCallback?.();

  if (!carousel.id) return;
  const navigation = document.querySelector(`carousel-navigation[aria-controls="${CSS.escape(carousel.id)}"]`);
  navigation?.onNavigationChange?.(visible.indexOf(selected));
}

class LayersBlock extends LayersRelatedProducts {}

if (typeof window !== 'undefined' && !window.customElements.get('layers-block')) {
  window.customElements.define('layers-block', LayersBlock);
}

if (typeof window !== 'undefined' && !window.customElements.get('layers-related-products')) {
  window.customElements.define('layers-related-products', LayersRelatedProducts);
}
